import React, { useCallback, useMemo, useState } from 'react';

import { faCircleNotch, faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useCreateChronologyFactWithSync } from '@/api/mutations/useCreateChronologyFactWithSync';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import Button from '@/components/atoms/Button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import SingleSelect from '@/components/atoms/Select';
import { ClassificationOptions, RelevanceOptions } from '@/constants/filterOptions';
import { Classification, FilterOption, MyOptionType, Relevance } from '@/types';

interface FactAdderProps {
  caseId: string;
  handleNewFactCreated: () => void;
  chronologyId: string;
}

const FactAdder = ({ caseId, chronologyId, handleNewFactCreated }: FactAdderProps) => {
  const [selectedDoc, setSelectedDoc] = useState<FilterOption | null>(null);
  const [significance, setSignificance] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [factDateText, setFactDateText] = useState<string>('');
  const [selectedRelevance, setSelectedRelevance] = useState<MyOptionType>({
    label: 'Yes',
    value: Relevance.Relevant,
  });
  const [selectedClassification, setSelectedClassification] = useState<MyOptionType>({
    label: 'Primary',
    value: Classification.Primary,
  });

  const { data: docIdsResponse } = useGetDocIds(caseId);
  const { mutateAsync: createChronologyFact, isLoading: isLoadingAddNewFact } = useCreateChronologyFactWithSync(
    chronologyId,
    selectedDoc?.value ?? '',
    caseId,
  );

  const handleChangeDescription = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  const handleChangeSelectedDoc = useCallback((newValue: FilterOption | null) => {
    setSelectedDoc(newValue);
  }, []);

  const handleChangeDateOfSubjectMatterText = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFactDateText(e.target.value);
  }, []);

  const handleChangeSignificance = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSignificance(e.target.value);
  }, []);

  const docsOptions = useMemo(
    () => docIdsResponse?.documents?.map((doc) => ({ label: doc.file.name, value: doc.id })) ?? [],
    [docIdsResponse],
  );

  const handleAddFact = async () => {
    await createChronologyFact({
      description,
      factDateText,
      significance,
      classification: selectedClassification?.value as Classification,
      relevance: selectedRelevance?.value as Relevance,
    });
    handleNewFactCreated();
  };

  const disabled = !selectedDoc || !description || !factDateText;

  return (
    <>
      {isLoadingAddNewFact ? (
        <div className="flex h-72 flex-col items-center justify-center gap-8">
          <div className="text-xl">Adding Fact...</div>
          <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-brandSecondary text-4xl" />
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-6 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="description">Fact Description</Label>
            <Input
              id="description"
              onChange={handleChangeDescription}
              value={description}
              placeholder="Enter fact description"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="document">Document</Label>
            <SingleSelect
              options={docsOptions}
              selectedOption={selectedDoc}
              onChange={handleChangeSelectedDoc}
              placeholder="Select a document"
              className="w-full text-sm"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="factDate">Date of Fact</Label>
            <Input
              id="factDate"
              onChange={handleChangeDateOfSubjectMatterText}
              value={factDateText}
              placeholder="Enter date of fact"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="significance">
              Significance <i className="text-gray-500">(Optional)</i>
            </Label>
            <Input
              id="significance"
              onChange={handleChangeSignificance}
              value={significance}
              placeholder="Enter significance"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex w-1/2 flex-col gap-2">
              <Label htmlFor="relevance">Relevance</Label>
              <SingleSelect
                options={Object.values(RelevanceOptions)}
                selectedOption={selectedRelevance}
                onChange={(newValue) => setSelectedRelevance(newValue)}
                placeholder="Select relevance"
                className="w-full text-sm"
              />
            </div>

            <div className="flex w-1/2 flex-col gap-2">
              <Label htmlFor="classification">Classification</Label>
              <SingleSelect
                options={Object.values(ClassificationOptions)}
                selectedOption={selectedClassification}
                onChange={(newValue) => setSelectedClassification(newValue)}
                placeholder="Select classification"
                className="w-full text-sm"
              />
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-md bg-gray-50 p-3">
            <div className="flex-1">
              <p className="text-xs text-gray-600">
                This will create a fact, meaning this fact will be visible in the table of facts and on the list of
                facts for the selected document.
              </p>
            </div>
          </div>

          <div className="mt-2">
            <Button
              onClick={handleAddFact}
              disabled={disabled}
              text="Add"
              type="primary"
              rounded="base"
              icon={<FontAwesomeIcon icon={faCirclePlus} className="mr-2" />}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FactAdder;
